import React from 'react';
import { MapPin, Clock, Plane, Car, Anchor, Heart } from 'lucide-react';
import { propertyData, sectionImages } from '../../data/mock';

const LocationSection = () => {
  const getIcon = (name) => {
    if (name.includes('Airport')) return Plane;
    if (name.includes('Lake') || name.includes('Spring') || name.includes('Beach')) return Anchor;
    if (name.includes('Hospital') || name.includes('Medical')) return Heart;
    return MapPin;
  };

  // Group destinations by approximate time
  const nearbyDestinations = propertyData.location.destinations.filter(d => 
    d.time.includes('10') || d.time.includes('15') || d.time.includes('20')
  );
  const mediumDestinations = propertyData.location.destinations.filter(d => 
    d.time.includes('25') || d.time.includes('35') || d.time.includes('45')
  );
  const furtherDestinations = propertyData.location.destinations.filter(d => 
    d.time.includes('50') || d.time.includes('60')
  );

  return (
    <section className="py-24 bg-gradient-to-b from-[#0d1117] to-[#0a0c10]" data-testid="location-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-400 text-sm font-medium tracking-wide mb-4">
            Location Intelligence
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white mb-4">
            {propertyData.location.headline}
          </h2>
          <p className="text-xl text-amber-400 font-light mb-4">
            {propertyData.location.subheadline}
          </p>
          <p className="text-lg text-white/70 font-light">
            {propertyData.location.description}
          </p>
        </div>

        {/* Destinations Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {/* Nearby */}
          <div className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-green-400 font-semibold">NEARBY</p>
                <p className="text-white/50 text-xs">10-20 minutes</p>
              </div>
            </div>
            <ul className="space-y-3">
              {nearbyDestinations.map((dest) => (
                <li key={dest.name} className="flex items-start">
                  <MapPin className="w-4 h-4 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-white/80 text-sm">{dest.name}</p>
                    <p className="text-white/40 text-xs">{dest.time}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Medium Distance */}
          <div className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Car className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-amber-400 font-semibold">EASY ACCESS</p>
                <p className="text-white/50 text-xs">25-50 minutes</p>
              </div>
            </div>
            <ul className="space-y-3">
              {mediumDestinations.map((dest) => (
                <li key={dest.name} className="flex items-start">
                  {dest.name.includes('Airport') ? (
                    <Plane className="w-4 h-4 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                  ) : (
                    <MapPin className="w-4 h-4 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                  )}
                  <div>
                    <p className="text-white/80 text-sm">{dest.name}</p>
                    <p className="text-white/40 text-xs">{dest.time}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Further */}
          <div className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                <Plane className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-blue-400 font-semibold">REGIONAL</p>
                <p className="text-white/50 text-xs">50-60 minutes</p>
              </div>
            </div>
            <ul className="space-y-3">
              {furtherDestinations.map((dest) => (
                <li key={dest.name} className="flex items-start">
                  <MapPin className="w-4 h-4 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-white/80 text-sm">{dest.name}</p>
                    <p className="text-white/40 text-xs">{dest.time}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Map Image */}
        <div className="relative max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-2xl">
          <img 
            src={sectionImages.birdsEye2}
            alt="Property aerial location"
            className="w-full h-64 md:h-80 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6">
            <div className="flex items-center space-x-2 text-white">
              <MapPin className="w-5 h-5 text-amber-400" />
              <span className="font-light">{propertyData.address}</span>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-center text-white/40 text-xs mt-8">
          {propertyData.location.disclaimer}
        </p>
      </div>
    </section>
  );
};

export default LocationSection;
